import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate } from "react-router-dom";
import { fetchMyEventList } from "../services/adminApiService/venueManagerApi/venueManagerApi";
import {
  Card,
  Container,
  Row,
  Col,
  Form,
  Button,
  Modal,
} from "react-bootstrap";
import { MdInsertChartOutlined } from "react-icons/md";
import { BiScan } from "react-icons/bi";
import Swal from "sweetalert2";
import { checkTicketById } from "../services/adminApiService/venueManagerApi/venueManagerApi";
import "./styles.css";

function Dashboard() {
  const [bookingId, setBookingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const isScanHandled = useRef(false);
  const qrScannerRef = useRef(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const limit = 10;

  const startScanner = () => setShowScanner(true);

  const stopScanner = () => {
    if (isScanning && qrScannerRef.current) {
      qrScannerRef.current
        .stop()
        .then(() => {
          qrScannerRef.current.clear();
          setShowScanner(false);
          setIsScanning(false);
        })
        .catch((err) => {
          console.warn("Error stopping scanner:", err);
        });
    } else {
      setShowScanner(false);
    }
  };

    const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`; // "11 Oct, 2025"
  };

  const verifyTicket = async (ticketId, sig) => {
    try {
      let token = localStorage.getItem("access_token_admin");
      const response = await axios.get(
        `${process.env.REACT_APP_TICKET_URL}/api/v1/ticket/verify`,
        {
          params: { ticketId, sig },
          headers: {
            Authorization: `${token}`,
          },
        },
      );
      const bookingId = response?.data?.data?.bookingId;

      Swal.fire({
        icon: "success",
        title: "Success",
        text: response.data.message,
      });
    } catch (error) {
      console.error("Verify error:", error);

      Swal.fire(
        "Verification Failed",
        error?.response?.data?.message || "Invalid ticket",
        "error",
      );
    }
  };

  useEffect(() => {
    if (showScanner) {
      const html5QrCode = new Html5Qrcode("qr-reader");
      qrScannerRef.current = html5QrCode;
      isScanHandled.current = false;

      html5QrCode
        .start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            if (isScanHandled.current) return;
            isScanHandled.current = true;

            console.log(decodedText, "decodedText");

            try {
              // ✅ Detect QR with ticket verification data
              if (
                decodedText.includes("ticketId") &&
                decodedText.includes("sig")
              ) {
                try {
                  const parsed = new URL(decodedText);

                  const ticketId = parsed.searchParams.get("ticketId");
                  const sig = parsed.searchParams.get("sig");

                  if (!ticketId || !sig) {
                    Swal.fire(
                      "Invalid QR Code",
                      "Missing ticket details",
                      "error",
                    );
                    stopScanner();
                    return;
                  }

                  await verifyTicket(ticketId, sig);
                  stopScanner();
                  return;
                } catch (err) {
                  console.error("URL parse error:", err);
                  Swal.fire("Invalid QR format");
                  stopScanner();
                  return;
                }
              }

              // ❌ Only fallback if NOT a ticket QR
              let bookingId;

              if (
                decodedText.trim().startsWith("{") &&
                decodedText.trim().endsWith("}")
              ) {
                const parsed = JSON.parse(decodedText);
                bookingId = parsed.booking_id;
              } else {
                const lastSegment = decodedText.substring(
                  decodedText.lastIndexOf("/") + 1,
                );
                bookingId = lastSegment.split("_")[0];
              }

              await handleCheck(bookingId); // fallback only
              stopScanner();
            } catch (err) {
              console.error("QR scan handling error:", err);
              Swal.fire("Error processing QR code.");
              stopScanner();
            }
          },
          (error) => {
            // Optional: console.log("Scan error:", error);
          },
        )
        .then(() => setIsScanning(true))
        .catch((err) => {
          console.error("QR Scanner start error:", err);
          Swal.fire("Unable to start QR scanner. Please check camera access.");
        });
    }

    return () => {
      const stopAndClearScanner = async () => {
        if (qrScannerRef.current) {
          try {
            if (isScanning) {
              await qrScannerRef.current.stop();
            }
            await qrScannerRef.current.clear();
          } catch (err) {
            console.error("Error stopping or clearing QR scanner:", err);
          }
        }
      };
      stopAndClearScanner();
    };
  }, [showScanner]);

  useEffect(() => {
    const getMyEvents = async () => {
      const bookings = await fetchMyEventList({
        page,
        limit,
        search: "",
        sportFilter: "",
        dateFilter: "",
        statusFilter: true,
        feeType: "",
        clusterFilter: "",
        venueFilter: "",
      });

      setData(bookings?.data[0] || []);
    };

    getMyEvents();
  }, [page]);

  const handleCheck = async (bookingId) => {
    setLoading(true);

    try {
      const response = await checkTicketById(bookingId);

      if (response?.data?.success) {
        const bookingDetails = response?.data?.bookingDetails;

        // ❗ Check if bookingDetails is empty
        if (!bookingDetails || Object.keys(bookingDetails).length === 0) {
          Swal.fire({
            icon: "error",
            title: "Ticket Not Found",
            text: "Ticket details not found.",
            showCloseButton: true,
          });
          return; // stop further execution
        }

        localStorage.setItem(
          "venueManagerBookingId",
          response?.data?.bookingDetails[0]?.ticketId,
        );

        //  return;
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          confirmButtonText: "Go to Ticket Details",
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = `ticket-details/${response?.data?.bookingDetails[0]?.ticketId}`;
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.data?.message || "Something went wrong.",
          showCloseButton: true,
        });
      }
    } catch (error) {
      console.error("Booking check error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
        showCloseButton: true,
      });
    } finally {
      setLoading(false);
      setBookingId("");
    }
  };

  return (
    <>
      {loading ? (
        <div className="text-center my-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <Container className="my-0">
          <div className="mx-3 pb-3 fs-3">Dashboard</div>

          <Card className="text-white text-center user-booking-stats">
            <Card.Body>
              <h4 className="mb-0 text-white">
                <MdInsertChartOutlined className="fs-1" /> User Bookings
              </h4>
            </Card.Body>
          </Card>
          <Row
            className="align-items-center px-2 py-4 mb-3"
            style={{
              background: "#f5f5f5",
              borderRadius: "12px",
              border: "1px solid #e5e5e5",
              maxWidth: "600px",
              backgroundColor: "white",
            }}
          >
            <Col>
              <p className="mb-1 text-muted large">Event Name</p>
              <h5 className="mb-0 fw-semibold">{data?.name}</h5>
            </Col>

            {/* Divider */}
            <Col xs="auto">
              <div
                style={{
                  width: "1px",
                  height: "40px",
                  backgroundColor: "#ccc",
                }}
              />
            </Col>

            <Col>
              <p className="mb-1 text-muted large">Duration</p>
              <h5 className="mb-0 fw-semibold">{formatDate(data?.start_date)} - {formatDate(data?.end_date)}</h5>
            </Col>
          </Row>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0">Event Bookings</h3>

            <Button
              className="fs-5 d-flex align-items-center gap-2"
              style={{
                background: "linear-gradient(to right, #8e2de2, #4a00e0)",
                border: "none",
                width: "220px",
              }}
              onClick={() => navigate("/event-booking")}
            >
              View User Event Bookings ↗
            </Button>
          </div>

          <Row className="mt-4">
            <Col md={6} className="mb-3">
              <Card
                className="text-center p-4"
                onClick={startScanner}
                style={{
                  backgroundColor: "#f4ebfa",
                  border: "1px solid #ccc",
                  borderRadius: "0.75rem",
                  cursor: "pointer",
                }}
              >
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ fontSize: "2rem", color: "#6f42c1" }}
                >
                  <div className="scanner">
                    <BiScan className="fs-3" />
                  </div>
                </div>
                <h5 className="mt-2 fw-bold fs-4">Scan QR Code</h5>
                <p className="text-muted mb-0 fs-4">
                  Allow users to Venue area by scanning there QR code.
                </p>
              </Card>
            </Col>

            {/* QR Scanner Modal */}
            <Modal show={showScanner} onHide={stopScanner} centered>
              <Modal.Header closeButton>
                <Modal.Title>Scan QR Code</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div id="qr-reader" style={{ width: "100%" }} />
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={stopScanner}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            <Col md={6} className="mb-3">
              <Card
                className="p-4 text-center"
                style={{
                  backgroundColor: "#f4ebfa",
                  border: "1px solid #ccc",
                  borderRadius: "0.75rem",
                }}
              >
                <h5 className="fw-bold mb-3 fs-4">Ticket ID Number</h5>
                <Form.Control
                  type="text"
                  placeholder="Enter ticket id"
                  className="mb-3"
                  value={bookingId}
                  onChange={(e) => setBookingId(e.target.value)}
                  style={{ textAlign: "center" }}
                />
                <div className="text-center">
                  <Button
                    className="fs-5"
                    style={{
                      background: "linear-gradient(to right, #8e2de2, #4a00e0)",
                      border: "none",
                      width: "150px",
                    }}
                    onClick={() => handleCheck(bookingId)}
                  >
                    Verify
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
}

export default Dashboard;
